/**
 * Everything that differs between the chapters of Vihangam Yog that run
 * on this platform. One typed row per chapter; every surface (backend,
 * admin, emails) reads from here instead of carrying its own literal.
 *
 * A chapter is a deployment: its own Worker, D1, Clerk, Stripe and
 * Postmark. This table is the only thing the deployments share.
 */

export const CHAPTER_IDS = ["aus", "nz", "sg"] as const;
export type Chapter = (typeof CHAPTER_IDS)[number];

export function isChapter(value: string): value is Chapter {
  return CHAPTER_IDS.some((id) => id === value);
}

/** Admin surfaces a chapter shows. Order is the nav order. */
export type AdminFeature =
  | "overview"
  | "events"
  | "varanasi-trip"
  | "reception"
  | "panel-questions"
  | "members"
  | "emails"
  | "inquiries"
  | "event-feedback"
  | "app-interest"
  | "roles"
  | "feedback"
  | "settings";

/** The palette + type the email templates are themed with. */
export interface EmailBrand {
  /** Buttons, links, rules. */
  accent: string;
  /** Lighter accent for hover and secondary links. */
  accentSoft: string;
  /** Body text. */
  ink: string;
  /** Page ground. */
  paper: string;
  /** Card ground inside the email body. */
  card: string;
  /** Headline face, as a CSS font-family value. */
  fontDisplay: string;
  /** Body face. */
  fontBody: string;
  /** Scripture / italic face. */
  fontSerif: string;
  /**
   * The small attribution under the email footer's motto. Australia and
   * New Zealand are initiatives of the Sansthan; Singapore is an
   * independent organisation and shows none.
   */
  footerAttribution?: string;
}

export interface ChapterConfig {
  id: Chapter;
  /** Display name: email header, sign-off, admin title. */
  name: string;
  /** Registered entity name, for receipts and bank details. */
  legalName: string;
  siteHost: string;
  siteUrl: string;
  adminHost: string;
  currency: "aud" | "nzd" | "sgd";
  /** IANA zone; reminders and "today" logic use it. */
  timezone: string;
  /** Region woven into flowing sentences ("in Sydney"). */
  region: string;
  /** "online and in ${reach}" copy. */
  reach: string;
  contactInbox: string;
  refPrefix: "VYA" | "VYS";
  /**
   * Stripe Checkout payment_method_types, in display order. Empty means
   * let Stripe choose from the account's automatic payment methods.
   */
  stripePaymentMethods: readonly ("card" | "paynow")[];
  /**
   * Keep Checkout in the chapter's own currency. Stripe's adaptive
   * pricing otherwise offers a visitor their local currency, which for a
   * Singapore-only event hides PayNow behind a currency switch.
   */
  stripeLocalCurrencyOnly: boolean;
  features: readonly AdminFeature[];
  /** Phone on the "how to find us" email, null when the chapter has none. */
  directionsPhone: string | null;
  /** Absolute URL to the chapter mark, served from its own domain. */
  logoUrl: string;
  emailBrand: EmailBrand;
}

const AUS_EMAIL_BRAND: EmailBrand = {
  accent: "#c15f3c",
  accentSoft: "#d97757",
  ink: "#1f1d1a",
  paper: "#f4f0e6",
  card: "#fbf8f1",
  fontDisplay: "'Marcellus','Times New Roman',Georgia,serif",
  fontBody: "'Alegreya Sans',-apple-system,Helvetica,Arial,sans-serif",
  fontSerif: "'Alegreya',Georgia,serif",
  footerAttribution:
    "An initiative of Sadguru Sadafaldeo Vihangam Yog Sansthan",
};

const ALL_FEATURES: readonly AdminFeature[] = [
  "overview",
  "events",
  "varanasi-trip",
  "reception",
  "panel-questions",
  "members",
  "emails",
  "inquiries",
  "event-feedback",
  "app-interest",
  "roles",
  "feedback",
  "settings",
];

/**
 * Everything except the Feedback board. The board promises a reply within
 * minutes from an assistant that is only watching the Singapore chapter, so
 * it stays off elsewhere until someone is watching there too.
 */
const FEATURES_WITHOUT_FEEDBACK: readonly AdminFeature[] = ALL_FEATURES.filter(
  (feature) => feature !== "feedback",
);

/** The form after the September 2026 visit and the Varanasi trip form
 *  are Australian, so New Zealand has no page for either. */
const NZ_FEATURES: readonly AdminFeature[] = FEATURES_WITHOUT_FEEDBACK.filter(
  (feature) => feature !== "event-feedback" && feature !== "varanasi-trip",
);

export const CHAPTERS = {
  aus: {
    id: "aus",
    name: "Vihangam Yog Australia",
    legalName: "Sadguru Sadafal Deo Vihangam Yog Sansthan Australia",
    siteHost: "vyaustralia.org",
    siteUrl: "https://vyaustralia.org",
    adminHost: "admin.vyaustralia.org",
    currency: "aud",
    timezone: "Australia/Sydney",
    region: "Sydney",
    reach: "Sydney",
    contactInbox: "info@vyaustralia.org",
    refPrefix: "VYA",
    stripePaymentMethods: [],
    stripeLocalCurrencyOnly: false,
    features: FEATURES_WITHOUT_FEEDBACK,
    directionsPhone: "0405 513 700",
    logoUrl: "https://vyaustralia.org/logo.png",
    emailBrand: AUS_EMAIL_BRAND,
  },
  nz: {
    id: "nz",
    name: "Vihangam Yog New Zealand",
    legalName: "Vihangam Yog New Zealand",
    siteHost: "vihangamyognz.org",
    siteUrl: "https://vihangamyognz.org",
    adminHost: "admin.vyaustralia.org",
    currency: "nzd",
    timezone: "Pacific/Auckland",
    region: "New Zealand",
    reach: "New Zealand",
    contactInbox: "info@vihangamyognz.org",
    refPrefix: "VYA",
    stripePaymentMethods: [],
    stripeLocalCurrencyOnly: false,
    features: NZ_FEATURES,
    directionsPhone: null,
    logoUrl: "https://vihangamyognz.org/logo.png",
    emailBrand: AUS_EMAIL_BRAND,
  },
  sg: {
    id: "sg",
    name: "Vihangam Yoga Singapore",
    legalName: "VIHANGAM YOGA SINGAPORE LTD.",
    // The chapter lives on the .org; the .com still serves the old site.
    siteHost: "vihangamsg.org",
    siteUrl: "https://vihangamsg.org",
    adminHost: "admin.vihangamsg.org",
    currency: "sgd",
    timezone: "Asia/Singapore",
    region: "Singapore",
    reach: "Singapore",
    contactInbox: "vysingapore@gmail.com",
    refPrefix: "VYS",
    stripePaymentMethods: ["card", "paynow"],
    stripeLocalCurrencyOnly: true,
    features: [
      "overview",
      "events",
      "reception",
      "inquiries",
      // No "emails": the automatic sends are on, but the hand-run email
      // desk is not set up for Singapore and would only confuse the team.
      "roles",
      "feedback",
      "settings",
    ],
    directionsPhone: "+65 9011 7690",
    // A 224px copy of the site mark; the full logo is 1.4k px and 780 KB.
    logoUrl: "https://vihangamsg.org/logo-email.png",
    emailBrand: {
      accent: "#be0a50",
      accentSoft: "#4aa7b4",
      ink: "#2a2420",
      paper: "#f5ead2",
      card: "#fdf7ea",
      fontDisplay: "'Libre Baskerville',Georgia,'Times New Roman',serif",
      fontBody: "'Montserrat',-apple-system,Helvetica,Arial,sans-serif",
      fontSerif: "'Libre Baskerville',Georgia,serif",
    },
  },
} satisfies Record<Chapter, ChapterConfig>;

export function chapterConfig(chapter: Chapter): ChapterConfig {
  return CHAPTERS[chapter];
}
